import { ReactNode, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { builderAnswersState } from 'states';
import { isDataComplete, isSourceComplete } from 'helpers/kpiBuilder/engine';
import { BuilderAnswers } from 'helpers/kpiBuilder/types';
import { BuilderStepNav, StepButtons, StepInfo } from './BuilderStepNav';
import { DetailsStep } from './DetailsStep';
import { GeneratedJson } from './GeneratedJson';
import { AdvancedStep } from './AdvancedStep';
import { CustomiseStep } from './CustomiseStep';
import { ExtraOptionsStep } from './ExtraOptionsStep';
import { FilterRowsStep } from './FilterRowsStep';
import { FunnelStep } from './FunnelStep';
import { TemplateStep } from './TemplateStep';
import { LearnerGroupsStep } from './LearnerGroupsStep';
import { DataSourceStep, hasDataStep, QuestionStep } from './QuestionStep';

// Typed conditions apply to most questions, and to a copied KPI once one is chosen; a gap has its own two lists.
const showsFilters = (a: BuilderAnswers, sourceChosen: boolean): boolean =>
  a.recipe === 'template' ? !!a.template.code : sourceChosen && a.recipe !== 'gap' && a.recipe !== 'progress';

// Advanced options apply to every question except a copy or a funnel, which keep their stored queries.
const showsExtras = (a: BuilderAnswers): boolean => (isDataComplete(a) || a.recipe === 'advanced') && a.recipe !== 'template' && a.recipe !== 'funnel';

// Everything that follows once the data is chosen.
const SourceSections = () => (
  <>
    <LearnerGroupsStep />
    <DetailsStep />
  </>
);

interface RecipeStepProps {
  a: BuilderAnswers;
}

// What each special question shows in place of the data and detail steps.
const OwnSteps = ({ a }: RecipeStepProps) => (
  <>
    {a.recipe === 'funnel' && <FunnelStep />}
    {a.recipe === 'template' && <TemplateStep />}
    {a.recipe === 'advanced' && <AdvancedStep />}
  </>
);

// Advanced options that apply once the question is answered.
const AdvancedOptions = ({ a }: RecipeStepProps) => (
  <>
    <ExtraOptionsStep />
    {a.recipe !== 'advanced' && <CustomiseStep />}
  </>
);

const ownSteps = ['funnel', 'template', 'advanced'];

interface DataStep extends StepInfo {
  node: ReactNode;
}

// The steps that apply to the answers so far: question, data, details, conditions, options. Each opens once the one before is answered.
function stepsFor(a: BuilderAnswers, sourceChosen: boolean): DataStep[] {
  const own = ownSteps.includes(a.recipe ?? '');
  const steps: DataStep[] = [{ id: 'question', title: 'Question', done: !!a.recipe, node: <QuestionStep /> }];
  if (hasDataStep(a)) steps.push({ id: 'data', title: 'Data', done: isSourceComplete(a), node: <DataSourceStep /> });
  if (own || sourceChosen) steps.push({ id: 'details', title: 'Details', done: isDataComplete(a), node: own ? <OwnSteps a={a} /> : <SourceSections /> });
  if (showsFilters(a, sourceChosen) && !own) steps.push({ id: 'conditions', title: 'Conditions', done: true, node: <FilterRowsStep /> });
  if (showsExtras(a)) steps.push({ id: 'options', title: 'Options', done: true, node: <AdvancedOptions a={a} /> });
  return steps;
}

// Data tab in builder mode: questions instead of a JSON query, one step at a time with Back and Next.
export const BuilderDataTab = () => {
  const answers = useRecoilValue(builderAnswersState);
  const [at, setAt] = useState(0);
  const top = useRef<HTMLDivElement>(null);
  const steps = stepsFor(answers, isSourceComplete(answers) && !ownSteps.includes(answers.recipe ?? ''));
  const current = Math.min(at, steps.length - 1);
  const go = (i: number) => {
    setAt(i);
    // jsdom has no scrolling; in the browser this brings the new step to the top.
    top.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div ref={top} className="col-sm-12 col-md-12 col-lg-8 mt-4 mb-3 pb-5">
      <BuilderStepNav steps={steps} current={current} onGo={go} />
      {steps[current].node}
      <StepButtons steps={steps} current={current} onGo={go} />
      <GeneratedJson kind="query" />
    </div>
  );
};
